import { plainToClass } from 'class-transformer';

import { Auth0Setting, Auth0Config, Auth0Connection } from './auth0';

/**
 * Contract of ExtraParams of Auth0 internal configuration that is available on server
 *
 * @interface Auth0ExtraParamsOptions
 */
interface Auth0ExtraParamsOptions {
  protocol: string;
  response_type: string;
  scope: string;
  state: string;
  csrf: string;
  brand: string;
  wauth: string;
  nonce: string;
  type: string;
}

/**
 * Contract for Auth0 authorization server options
 *
 * @interface Auth0AuthorizationServerOptions
 */
interface Auth0AuthorizationServerOptions {
  issuer: string;
}

/**
 * Contract for Auth0 internal configuration that is available on server
 *
 * @interface Auth0ConfigJson
 */
interface Auth0InternalConfiguration {
  auth0Domain: string;
  clientID: string;
  callbackURL: string;
  auth0Tenant: string;
  authorizationServer: Auth0AuthorizationServerOptions;
  extraParams: Auth0ExtraParamsOptions;
}

/**
 * Json representation of ExtraParams of Auth0 internal configuration that is available on server
 *
 * @class JsonAuth0ExtraParamsOptions
 * @implements Auth0ExtraParamsOptions
 */
class JsonAuth0ExtraParamsOptions implements Auth0ExtraParamsOptions {
  scope = '';

  response_type = '';

  type = '';

  csrf = '';

  state = '';

  nonce = '';

  protocol = '';

  brand = '';

  wauth = '';
}

class JsonAuth0AuthorizationServerOptions
  implements Auth0AuthorizationServerOptions {
  issuer = '';
}

/**
 * Json representation of Auth0 internal configuration that is available on server
 *
 * @class JsonAuth0InternalConfiguration
 * @implements Auth0InternalConfiguration
 */
class JsonAuth0InternalConfiguration implements Auth0InternalConfiguration {
  auth0Domain = '';

  clientID = '';

  callbackURL = '';

  auth0Tenant = '';

  authorizationServer: Auth0AuthorizationServerOptions = new JsonAuth0AuthorizationServerOptions();

  extraParams: Auth0ExtraParamsOptions = new JsonAuth0ExtraParamsOptions();
}

/**
 * Vistaprint Auth0 configuration
 *
 * @exports
 * @class VpAuth0Config
 * @implements Auth0Config
 */
export class VpAuth0Config implements Auth0Config {
  private connectionName = 'Monolith-Authentication';

  private siteName = 'Vistaprint';

  private config: Auth0InternalConfiguration;

  private setting: Auth0Setting;

  constructor(setting: Auth0Setting) {
    this.setting = setting;
    const jsonObject = JSON.parse(
      decodeURIComponent(escape(window.atob(setting.auth0Settings)))
    );

    const fooInstance = plainToClass(
      JsonAuth0InternalConfiguration,
      jsonObject,
      { excludePrefixes: ['_'] }
    );
    this.config = fooInstance as Auth0InternalConfiguration;
  }

  public get domain(): string {
    return this.config.auth0Domain;
  }

  public get clientId(): string {
    return this.config.clientID;
  }

  public get callbackUrl(): string {
    return this.config.callbackURL;
  }

  public get scope(): string {
    return this.config.extraParams.scope;
  }

  public get connection(): string {
    // TODO: should be part of config (https://cdn.eu.auth0.com/client/2PprbliJJCVd7SV93iPGytogzYuuNSpZ.js)
    // return this.config.connection;
    return this.connectionName;
  }

  public get leeway(): number {
    return this.setting.leeway;
  }

  public get audience(): string {
    return this.setting.audience;
  }

  public get responseType(): string {
    if (this.config.extraParams.response_type) {
      return this.config.extraParams.response_type;
    }

    return this.config.extraParams.type;
  }

  public get csrf(): string {
    return this.config.extraParams.csrf;
  }

  public get state(): string {
    return this.config.extraParams.state;
  }

  public get protocol(): string {
    return this.config.extraParams.protocol;
  }

  public get nonce(): string {
    return this.config.extraParams.nonce;
  }

  public get brand(): string {
    return this.config.extraParams.brand;
  }

  public get connections(): Array<Auth0Connection> {
    return this.setting.connections;
  }

  public get site(): string {
    return this.siteName;
  }

  public get tenant(): string {
    if (this.config.auth0Tenant) {
      return this.config.auth0Tenant;
    }

    return this.setting.auth0Tenant;
  }

  public get wauth(): string {
    return this.config.extraParams.wauth;
  }

  public get authorizationServerIssuer(): string {
    return this.config.authorizationServer.issuer;
  }
}
